import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Fade,
  Paper,
  Button,
  Zoom,
  alpha,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Circle as CircleIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Photo as PhotoIcon,
  Work as WorkIcon,
  Cake as CakeIcon,
  Campaign as CampaignIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { NotificationType, NotificationPriority } from '../../types/user';

/**
 * NotificationBell - Beautiful, animated notification bell in header
 *
 * Features:
 * - Smooth animations and transitions
 * - Priority-based color coding
 * - Icon mapping for notification types
 * - Unread badge with pulse animation
 * - Elegant dropdown with rich notifications
 * - Hover effects and micro-interactions
 */
export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notificationId: string, actionUrl?: string) => {
    await markAsRead(notificationId);
    handleClose();

    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  // Get icon for notification type
  const getNotificationIcon = (type: NotificationType) => {
    const iconMap: Record<string, React.ReactElement> = {
      welcome: <PersonIcon />,
      approval_granted: <CheckCircleIcon />,
      approval_denied: <ErrorIcon />,
      role_changed: <PersonIcon />,
      content_approved: <ThumbUpIcon />,
      content_rejected: <ErrorIcon />,
      comment_mention: <CommentIcon />,
      comment_reply: <CommentIcon />,
      photo_tagged: <PhotoIcon />,
      research_note_assigned: <WorkIcon />,
      birthday_reminder: <CakeIcon />,
      anniversary_reminder: <CakeIcon />,
      system_announcement: <CampaignIcon />,
    };

    return iconMap[type] || <NotificationsIcon />;
  };

  // Get color for priority
  const getPriorityColor = (priority?: NotificationPriority) => {
    const colorMap = {
      [NotificationPriority.CRITICAL]: 'error.main',
      [NotificationPriority.HIGH]: 'warning.main',
      [NotificationPriority.NORMAL]: 'primary.main',
      [NotificationPriority.LOW]: 'info.main',
    };

    return colorMap[priority || NotificationPriority.NORMAL];
  };

  // Show first 5 notifications in dropdown
  const recentNotifications = notifications.slice(0, 5);

  return (
    <>
      {/* Notification Bell Button */}
      <Tooltip title="Notifications" arrow>
        <IconButton
          color="inherit"
          onClick={handleClick}
          aria-label={`${unreadCount} unread notifications`}
          sx={{
            position: 'relative',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              backgroundColor: alpha('#fff', 0.1),
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                animation: unreadCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.2)',
                  },
                },
              },
            }}
          >
            {unreadCount > 0 ? (
              <NotificationsActiveIcon
                sx={{
                  animation: 'ring 1s ease-in-out infinite',
                  '@keyframes ring': {
                    '0%, 100%': {
                      transform: 'rotate(0deg)',
                    },
                    '10%, 30%': {
                      transform: 'rotate(-10deg)',
                    },
                    '20%, 40%': {
                      transform: 'rotate(10deg)',
                    },
                  },
                }}
              />
            ) : (
              <NotificationsIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Notification Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 8,
          sx: {
            width: 400,
            maxHeight: 600,
            overflow: 'visible',
            mt: 1.5,
            borderRadius: 2,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                theme.palette.primary.main,
                0.05
              )} 100%)`,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                {unreadCount} unread
              </Typography>
            )}
          </Box>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read" arrow>
              <IconButton
                size="small"
                onClick={handleMarkAllAsRead}
                sx={{
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <MarkEmailReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        {/* Notifications List */}
        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {loading ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </MenuItem>
          ) : recentNotifications.length === 0 ? (
            <Box
              sx={{
                py: 6,
                px: 3,
                textAlign: 'center',
              }}
            >
              <NotificationsIcon
                sx={{
                  fontSize: 64,
                  color: 'text.disabled',
                  mb: 2,
                  opacity: 0.3,
                }}
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No new notifications
              </Typography>
              <Typography variant="caption" color="text.disabled">
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            recentNotifications.map((notification, index) => (
              <Zoom
                in={true}
                style={{ transitionDelay: `${index * 50}ms` }}
                key={notification.id}
              >
                <MenuItem
                  onClick={() =>
                    handleNotificationClick(notification.id, notification.actionUrl)
                  }
                  sx={{
                    py: 1.5,
                    px: 2.5,
                    borderLeft: notification.read ? 'none' : '4px solid',
                    borderColor: getPriorityColor(notification.priority),
                    bgcolor: notification.read
                      ? 'transparent'
                      : (theme) => alpha(theme.palette.primary.main, 0.05),
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: notification.read
                        ? (theme) => alpha(theme.palette.primary.main, 0.08)
                        : (theme) => alpha(theme.palette.primary.main, 0.12),
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: (theme) =>
                          alpha(
                            theme.palette[
                              notification.priority === NotificationPriority.CRITICAL
                                ? 'error'
                                : notification.priority === NotificationPriority.HIGH
                                ? 'warning'
                                : notification.priority === NotificationPriority.LOW
                                ? 'info'
                                : 'primary'
                            ].main,
                            0.1
                          ),
                        color: getPriorityColor(notification.priority),
                        fontSize: '1.2rem',
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.read ? 500 : 700,
                            flex: 1,
                            fontSize: '0.9rem',
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <CircleIcon
                            sx={{
                              fontSize: 8,
                              color: getPriorityColor(notification.priority),
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            mb: 0.5,
                            fontSize: '0.85rem',
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.disabled',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                          }}
                        >
                          {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                        </Typography>
                      </>
                    }
                  />
                </MenuItem>
              </Zoom>
            ))
          )}
        </Box>

        {/* Footer */}
        {recentNotifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Button
                fullWidth
                variant="text"
                onClick={handleViewAll}
                sx={{
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};
